/**
 * TUUFAM LUXURY CART ENGINE v3.1
 * ─────────────────────────────────────────────────────────────
 * • Dynamic Cart Psychology & Milestone Track Sync
 * • In-Cart Instant Size Switcher (Zero Deletion Swaps)
 * • 1-Tap "You May Also Like" Upsell Engine
 * • Kinetic Number Glide & Real-Time Savings Engine
 * • Touch Swipe-to-Dismiss & 2-Second Checkout Nudge Shuffle
 * ─────────────────────────────────────────────────────────────
 */
(() => {
  'use strict';

  /* ════════════════════════════════════════
     STATE & CONSTANTS
  ════════════════════════════════════════ */
  const CURRENCY = '₹';
  let isUpdating = false;
  let touchStartY = 0;
  let touchCurrentY = 0;
  let isDraggingDrawer = false;
  let shuffleInterval = null;

  /* ════════════════════════════════════════
     UTILITY: Format Indian Currency
  ════════════════════════════════════════ */
  const inr = (amount) => `${CURRENCY}${Number(amount).toLocaleString('en-IN')}`;

  /* ════════════════════════════════════════
     KINETIC NUMBER GLIDE
  ════════════════════════════════════════ */
  const glideDigit = (el, newText) => {
    if (!el || el.textContent === newText) return;
    el.style.transition = 'transform 0.12s ease, opacity 0.12s ease';
    el.style.transform = 'translateY(-3px)';
    el.style.opacity = '0.3';
    setTimeout(() => {
      el.textContent = newText;
      el.style.transform = 'translateY(0)';
      el.style.opacity = '1';
    }, 120);
  };

  /* ════════════════════════════════════════
     DYNAMIC CART CALCULATIONS
  ════════════════════════════════════════ */
  const updatePsychology = (cart, skipBanner = false) => {
    const items = cart.items || [];
    const count = cart.item_count || 0;

    const totalRowBtn = document.getElementById('tfTotalRowBtn');
    const mrpPaise = items.reduce((sum, item) => {
      const unitMrp = (item.compare_at_price > 0 ? item.compare_at_price : null)
        || (item.original_price > item.price ? item.original_price : null)
        || (item.price === 129900 ? 180000 : item.price);
      return sum + (unitMrp * item.quantity);
    }, 0);

    if (totalRowBtn) {
      totalRowBtn.dataset.mrpPaise = mrpPaise;
    }

    const dynamicMrp = Math.round(mrpPaise / 100);
    const subtotal = Math.round((cart.total_price || 0) / 100);
    const discount = Math.max(0, dynamicMrp - subtotal);
    const totalSavings = discount + 50;

    /* 1. Header Count */
    document.querySelectorAll('.tf-header__count, #tfCartCount').forEach((el) => {
      el.textContent = `(${count})`;
    });

    /* 2. Milestone Progress Rail */
    const tierFill = document.getElementById('tfTierFill');
    if (tierFill) {
      tierFill.style.width = count >= 2 ? '100%' : '50%';
    }

    const node2 = document.getElementById('tfTierNode2');
    if (node2) {
      node2.classList.toggle('tf-rail-node--active', count >= 2);
      node2.innerHTML = count >= 2
        ? '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
        : '<svg width="9" height="11" viewBox="0 0 24 24" fill="#A1A1AA"><path d="M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4"/></svg>';
    }

    const railLbl2 = document.getElementById('tfRailLbl2');
    const railLblB2 = document.getElementById('tfRailLblB2');
    const railNudge = document.getElementById('tfRailNudge');
    if (railLbl2) railLbl2.classList.toggle('tf-rail-lbl--active', count >= 2);
    if (railLblB2) railLblB2.classList.toggle('tf-rail-lbl-b--active', count >= 2);
    if (railNudge) {
      railNudge.innerHTML = count >= 2
        ? '🔥 Max savings unlocked — you\'re all set!'
        : '⚡ Add <strong>1 more item</strong> → unlock ₹200 bundle off';
    }

    /* 3. Dynamic Savings Capsule & Headline */
    const headline = document.getElementById('tfUpiHeadline');
    const sub = document.getElementById('tfUpiSub');
    const upiPill = document.getElementById('tfUpiHeroPill');
    const upiAmount = document.getElementById('tfUpiHeroAmount');
    const upiLabel = document.getElementById('tfUpiHeroLabel');

    if (upiPill) upiPill.classList.toggle('tf-upi-hero-pill--bundle', count >= 2);
    if (upiAmount) upiAmount.textContent = count >= 2 ? '₹200' : '₹50';
    if (upiLabel) upiLabel.textContent = count >= 2 ? 'OFF' : 'UPI OFF';

    if (headline) {
      headline.innerHTML = count >= 2
        ? '<strong>MAX SAVINGS: ₹200 BUNDLE + ₹50 UPI UNLOCKED</strong>'
        : '<strong>FLAT ₹50 INSTANT OFF UNLOCKED</strong>';
    }
    if (sub) {
      sub.innerHTML = count >= 2
        ? '<span class="tf-upi-maxed">🔥 Maximum savings. You\'re maxed out.</span>'
        : 'Pay via UPI — ₹50 back instantly. <span class="tf-upi-nudge">Add 1 more item → unlock ₹200 extra</span>';
    }

    /* 4. Savings Banner */
    const banner = document.getElementById('tfSavingsBannerText');
    const bannerSub = document.getElementById('tfSavingsBannerSub');
    if (banner && count > 0 && !skipBanner) {
      if (count >= 2) {
        banner.textContent = `🔥 ${inr(totalSavings)} Saved so far!`;
        if (bannerSub) bannerSub.textContent = `₹200 Bundle + ₹50 UPI at checkout 🎯`;
      } else {
        banner.textContent = `🎉 ${inr(totalSavings)} Saved so far!`;
        if (bannerSub) bannerSub.textContent = `⚡ Save ₹50 extra · Select UPI at checkout`;
      }
    }

    /* 5. Estimated Total Row */
    const mrpEl = document.getElementById('tfTotalMrp');
    const finalEl = document.getElementById('tfTotalFinal');
    const offPillEl = document.getElementById('tfTotalOffPill');

    if (mrpEl) {
      mrpEl.textContent = inr(dynamicMrp);
      mrpEl.style.display = dynamicMrp > subtotal ? '' : 'none';
    }
    if (finalEl) glideDigit(finalEl, inr(subtotal));
    if (offPillEl) {
      if (dynamicMrp > subtotal && dynamicMrp > 0) {
        const pct = Math.round(((dynamicMrp - subtotal) / dynamicMrp) * 100);
        offPillEl.textContent = `(${pct}% OFF)`;
        offPillEl.style.display = '';
      } else {
        offPillEl.style.display = 'none';
      }
    }

    /* 6. Fused Price on Checkout CTA */
    const fusedAmount = document.getElementById('tfFusedAmount');
    if (fusedAmount && count > 0) {
      glideDigit(fusedAmount, inr(subtotal));
    }
  };

  /* ════════════════════════════════════════
     SHOPIFY CART API SYNC (GLOBAL BROADCAST)
  ════════════════════════════════════════ */
  const syncCart = (skipBanner = false) => {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => {
        if (!cart) return;
        updatePsychology(cart, skipBanner);
        
        /* Broadcast to Bottom Dock & Header */
        const count = cart.item_count || 0;
        document.querySelectorAll('.tuufam-dock-count-text, .cart-count-bubble span').forEach((el) => {
          el.textContent = count;
        });
        document.querySelectorAll('.tuufam-dock-bubble, .cart-count-bubble').forEach((el) => {
          el.style.display = count > 0 ? 'flex' : 'none';
        });

        // Trigger dynamic event for dock
        document.dispatchEvent(new CustomEvent('tuufam:cart-synced', { detail: { cart } }));
      })
      .catch((err) => console.warn('[TUUFAM] Cart sync error:', err));
  };

  /* ════════════════════════════════════════
     DRAWER SECTION RE-RENDER ENGINE (WITH PROMISE)
  ════════════════════════════════════════ */
  const reRenderDrawer = () => {
    return fetch('/cart?section_id=cart-drawer', { headers: { 'Accept': 'text/html' } })
      .then((r) => r.ok ? r.text() : null)
      .then((html) => {
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newInner = doc.querySelector('.drawer__inner');
        const currentInner = document.querySelector('.drawer__inner');

        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl && drawerEl.classList.contains('is-empty')) {
          drawerEl.classList.remove('is-empty');
        }

        syncCart();
        attachSwipeDismiss();
        setTimeout(startBtnShuffle, 700);
      })
      .catch((err) => console.warn('[TUUFAM] Re-render error:', err))
      .finally(() => { isUpdating = false; });
  };

  // Expose Globally
  window.reRenderTuufamDrawer = reRenderDrawer;
  window.syncTuufamCart = syncCart;

  /* ════════════════════════════════════════
     QUANTITY MUTATION
  ════════════════════════════════════════ */
  const mutateQty = (lineId, newQty, rowEl) => {
    if (isUpdating || (!lineId && lineId !== 0)) return;
    isUpdating = true;

    if (rowEl) {
      rowEl.style.transition = 'opacity 0.18s ease';
      rowEl.style.opacity = '0.35';
      rowEl.style.pointerEvents = 'none';
    }

    const isLineKey = typeof lineId === 'string' && lineId.includes(':');
    const payload = isLineKey
      ? { id: lineId, quantity: newQty }
      : { line: Number(lineId), quantity: newQty };

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then(() => reRenderDrawer())
      .catch((err) => {
        console.warn('[TUUFAM] Qty update error:', err);
        if (rowEl) { rowEl.style.opacity = '1'; rowEl.style.pointerEvents = ''; }
        isUpdating = false;
      });
  };

  /* ════════════════════════════════════════
     DELEGATED EVENT HANDLERS
  ════════════════════════════════════════ */
  const initControls = () => {
    if (document.body._tuufamCartBound) return;
    document.body._tuufamCartBound = true;

    document.body.addEventListener('click', (e) => {

      /* 1. In-Cart Size Switcher Dropdown Toggle */
      const sizePillBtn = e.target.closest('.tf-size-pill-btn');
      if (sizePillBtn) {
        e.preventDefault();
        const wrap = sizePillBtn.closest('.tf-variant-pill-wrap');
        const isOpen = wrap?.classList.contains('tf-size-open');
        document.querySelectorAll('.tf-variant-pill-wrap.tf-size-open').forEach((el) => el.classList.remove('tf-size-open'));
        if (!isOpen && wrap) wrap.classList.add('tf-size-open');
        return;
      }

      /* 2. In-Cart Size Selection (Variant Swap) */
      const sizeOptBtn = e.target.closest('.tf-size-opt-btn');
      if (sizeOptBtn) {
        e.preventDefault();
        if (sizeOptBtn.classList.contains('tf-size-opt-btn--active') || sizeOptBtn.disabled) return;
        const lineKey = sizeOptBtn.dataset.lineKey;
        const newVariantId = sizeOptBtn.dataset.newVariantId;
        const qty = parseInt(sizeOptBtn.dataset.qty, 10) || 1;
        const card = sizeOptBtn.closest('.tf-product-card');

        if (card) {
          card.style.opacity = '0.35';
          card.style.pointerEvents = 'none';
        }

        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        })
          .then(() => fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: qty }], sections: 'cart-drawer' })
          }))
          .then((r) => r.json())
          .then((data) => {
            if (data.sections && data.sections['cart-drawer']) {
              reRenderDrawer(data.sections['cart-drawer']);
            } else {
              reRenderDrawer();
            }
          })
          .catch((err) => {
            console.warn('[TUUFAM] Size swap error:', err);
            if (card) { card.style.opacity = '1'; card.style.pointerEvents = ''; }
          });
        return;
      }

      /* Close size popovers when clicking outside */
      if (!e.target.closest('.tf-variant-pill-wrap')) {
        document.querySelectorAll('.tf-variant-pill-wrap.tf-size-open').forEach((el) => el.classList.remove('tf-size-open'));
      }

      /* 3. Upsell Carousel Size Selection */
      const upsellSizeBtn = e.target.closest('.tf-upsell-size-btn');
      if (upsellSizeBtn) {
        e.preventDefault();
        const card = upsellSizeBtn.closest('.tf-upsell-card');
        if (!card) return;
        card.querySelectorAll('.tf-upsell-size-btn').forEach((b) => b.classList.remove('tf-upsell-size-btn--active'));
        upsellSizeBtn.classList.add('tf-upsell-size-btn--active');
        const newVarId = upsellSizeBtn.dataset.variantId;
        const addBtn = card.querySelector('.tf-upsell-add-btn');
        if (addBtn) addBtn.dataset.variantId = newVarId;
        if (upsellSizeBtn.dataset.price) {
          const priceEl = card.querySelector('.tf-upsell-price');
          if (priceEl) priceEl.textContent = upsellSizeBtn.dataset.price;
        }
        return;
      }

      /* 4. Upsell 1-Tap "+ ADD" Action */
      const upsellAddBtn = e.target.closest('.tf-upsell-add-btn');
      if (upsellAddBtn) {
        e.preventDefault();
        const card = upsellAddBtn.closest('.tf-upsell-card');
        const variantId = upsellAddBtn.dataset.variantId;
        if (!variantId || upsellAddBtn.disabled) return;
        upsellAddBtn.disabled = true;
        upsellAddBtn.innerHTML = '<span style="color:#10B981;font-weight:900;">✓ ADDED</span>';
        if (card) {
          card.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
          card.style.opacity = '0.4';
          card.style.pointerEvents = 'none';
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: 'cart-drawer' })
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.sections && data.sections['cart-drawer']) {
              reRenderDrawer(data.sections['cart-drawer']);
            } else {
              reRenderDrawer();
            }
          })
          .catch((err) => {
            console.warn('[TUUFAM] Upsell error:', err);
            upsellAddBtn.disabled = false;
            upsellAddBtn.innerHTML = '<span class="tf-upsell-btn-label">+ ADD</span>';
            if (card) { card.style.opacity = '1'; card.style.pointerEvents = ''; }
          });
        return;
      }

      /* 5. Quantity Stepper Plus */
      const plusBtn = e.target.closest('.tf-qty-btn--plus');
      if (plusBtn) {
        e.preventDefault();
        const row = plusBtn.closest('.tf-product-card');
        const input = row?.querySelector('.tf-qty-input');
        if (!input) return;
        const cur = parseInt(input.value, 10) || 0;
        const lineKey = input.dataset.quantityLineKey || input.dataset.key;
        const lineIdx = input.dataset.index;
        const newQty = cur + 1;
        input.value = newQty;
        mutateQty(lineKey || lineIdx, newQty, row);
        return;
      }

      /* 6. Quantity Stepper Minus */
      const minusBtn = e.target.closest('.tf-qty-btn--minus');
      if (minusBtn) {
        e.preventDefault();
        const row = minusBtn.closest('.tf-product-card');
        const input = row?.querySelector('.tf-qty-input');
        if (!input) return;
        const cur = parseInt(input.value, 10) || 0;
        const lineKey = input.dataset.quantityLineKey || input.dataset.key;
        const lineIdx = input.dataset.index;
        if (cur > 1) {
          const newQty = cur - 1;
          input.value = newQty;
          mutateQty(lineKey || lineIdx, newQty, row);
        } else {
          mutateQty(lineKey || lineIdx, 0, row);
        }
        return;
      }

      /* 7. Trash Button Removal */
      const trashBtn = e.target.closest('.tf-remove-btn');
      if (trashBtn) {
        e.preventDefault();
        const row = trashBtn.closest('.tf-product-card');
        const container = trashBtn.closest('cart-remove-button');
        const input = row?.querySelector('.tf-qty-input');
        const lineKey = trashBtn.dataset.key || input?.dataset.quantityLineKey || null;
        const lineIdx = trashBtn.dataset.index || container?.dataset.index || input?.dataset.index || null;
        mutateQty(lineKey || lineIdx, 0, row);
        return;
      }

      /* 8. Order Summary Toggle */
      const totalRowBtn = e.target.closest('#tfTotalRowBtn');
      if (totalRowBtn) {
        e.preventDefault();
        const summary = document.getElementById('tfOrderSummary');
        const isOpen = totalRowBtn.getAttribute('aria-expanded') === 'true';
        totalRowBtn.setAttribute('aria-expanded', !isOpen);
        summary?.classList.toggle('tf-summary-open', !isOpen);
        summary?.setAttribute('aria-hidden', isOpen);
        return;
      }

    });
  };

  /* ════════════════════════════════════════
     MOBILE TOUCH DISMISS (FIXED: ZERO DRAG INTERFERENCE)
  ════════════════════════════════════════ */
  const attachSwipeDismiss = () => {
    // Disabled drawer-level dragging so the scroll feed (products, milestone & upsells) scrolls naturally inside without moving the cart drawer
    const drawerInner = document.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.style.transform = '';
    }
  };

  /* ════════════════════════════════════════
     BUTTON 2-SECOND SHUFFLE
  ════════════════════════════════════════ */
  const startBtnShuffle = () => {
    const btn = document.getElementById('CartDrawer-Checkout');
    if (!btn) return;
    if (shuffleInterval) clearInterval(shuffleInterval);
    btn.classList.remove('tf-upi-nudge-on');
    shuffleInterval = setInterval(() => {
      btn.classList.toggle('tf-upi-nudge-on');
    }, 2000);
  };

  const watchDrawer = () => {
    const drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl) return;
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (drawerEl.classList.contains('active')) {
            setTimeout(startBtnShuffle, 700);
          } else {
            if (shuffleInterval) clearInterval(shuffleInterval);
            shuffleInterval = null;
          }
        }
      }
    }).observe(drawerEl, { attributes: true });
  };

  /* ════════════════════════════════════════
     LIFECYCLE INITIALIZATION
  ════════════════════════════════════════ */
  const init = () => {
    syncCart(false);
    initControls();
    attachSwipeDismiss();
    watchDrawer();

    document.body.addEventListener('click', (e) => {
      if (
        e.target.closest('.product-form__submit') ||
        e.target.closest('.quick-add__submit') ||
        e.target.closest('[data-product-form-submit]')
      ) {
        syncCart();
      }
    });

    document.addEventListener('cart:refresh', syncCart);
    document.addEventListener('cart:updated', syncCart);
    document.addEventListener('cart-drawer:open', () => {
      syncCart();
      setTimeout(startBtnShuffle, 700);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();